import React, { useEffect, useState } from 'react';
import { Pagination, Skeleton } from 'antd';
import {
  FlexWrapper,
  PaginationBox,
  PurpleText,
  SeeMore,
  TitleContainer,
  TitleH4
} from '../../../theme/common_style';
import { DeleteIcon, EditIcon, ReportNotFoundIcon } from '../../../theme/SvgIcons';
import EmptyData from '../../../components/common/EmptyData';
import dayjs from 'dayjs';
import CreateFeedback from './CreateFeedback';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { deleteProjectFeedbackApi, getProjectFeedbackApi } from '../../../redux/project/apiRoute';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { DateTime, NoteTitle, Description, NotesCard, GridContainer } from '../notes/Notes';
import ViewFeedback from './ViewFeedback';
import useProjectMemberOptions from '../../../hooks/useProjectMembers';
import { checkPermission, getFullName } from '../../../utils/common_functions';
import { useSelector } from 'react-redux';

const Feedback = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const location = useLocation();
  const { id } = location.state || {};
  const { loading: memberLoading, options } = useProjectMemberOptions(id);

  const { permissions } = useSelector((state) => state?.userInfo?.data);
  let permissionSection = 'Projects';
  const canDelete = checkPermission(permissionSection, 'del', permissions);
  const canUpdate = checkPermission(permissionSection, 'update', permissions);

  const [viewModal, setViewModal] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: false
  });

  const handleAction = (data, type) => {
    if (type === 'delete') {
      setDeleteModal(true);
    } else if (type === 'view') {
      setViewModal(true);
    } else {
      setOpen(true);
    }

    setSelectedFeedback(data);
  };

  const handleClose = (type) => {
    if (type === 'delete') {
      setDeleteModal(false);
    } else {
      setOpen(false);
    }
    setViewModal(false);
    setSelectedFeedback(null);
  };

  const description = (str) => {
    const length = str?.length;
    if (length > 100) {
      return str?.slice(0, 100).concat('...');
    } else {
      return str;
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current
    });
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      let res = await deleteProjectFeedbackApi(selectedFeedback?.id);
      if (res?.statusCode === 200) {
        getFeedbackData();
        handleClose('delete');
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getFeedbackData = async () => {
    try {
      setLoading(true);
      let params = new URLSearchParams();
      params.append('limit', pagination?.pageSize);
      params.append('page', pagination?.current);
      let res = await getProjectFeedbackApi(id, params);
      if (res?.statusCode === 200) {
        setData(res?.data?.notes || []);
        setPagination({
          ...pagination,
          total: res?.data?.total
        });
        setLoading(false);
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFeedbackData();
  }, [id]);

  return (
    <>
      {open && (
        <CreateFeedback
          open={open}
          onCancel={handleClose}
          edit={selectedFeedback}
          handleListing={getFeedbackData}
          member={options}
          memberLoading={memberLoading}
        />
      )}

      {viewModal && (
        <ViewFeedback open={viewModal} onCancel={handleClose} data={selectedFeedback} />
      )}

      {deleteModal && (
        <ConfirmationModal
          open={deleteModal}
          onCancel={() => handleClose('delete')}
          title="Delete Feedback"
          onSubmit={handleDelete}
          buttonName="Delete"
          description={`Are you sure you want to delete ${selectedFeedback?.title}?`}
          iconBG="#FB4A49"
          loading={deleteLoading}
        />
      )}

      {!loading && (
        <TitleContainer>
          <TitleH4>Feedbacks</TitleH4>
          <PurpleText
            style={{ cursor: 'pointer', width: 'fit-content' }}
            onClick={() => setOpen(true)}>
            + Create New Feedback
          </PurpleText>
        </TitleContainer>
      )}
      {loading ? (
        <GridContainer>
          {Array.from({ length: 4 })?.map((_, index) => (
            <div
              key={index}
              style={{
                width: '100%',
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '16px'
              }}>
              <Skeleton active title={false} paragraph={{ rows: 5 }} />
            </div>
          ))}
        </GridContainer>
      ) : data?.length === 0 ? (
        <EmptyData
          height={'70vh'}
          icon={<ReportNotFoundIcon />}
          title={'Feedbacks'}
          subTitle={'No Data Found'}
        />
      ) : (
        <>
          <GridContainer>
            {data?.map((el, index) => (
              <NotesCard key={index}>
                <FlexWrapper width="100%" justify="space-between" align="center" wrap="no-wrap">
                  <div className="content">
                    <NoteTitle>
                      {getFullName(
                        el?.employee?.first_name,
                        el?.employee?.middle_name,
                        el?.employee?.last_name
                      )}
                    </NoteTitle>
                    <DateTime>{dayjs(el?.created_at).format('DD MMM, YYYY')}</DateTime>
                  </div>
                  <FlexWrapper gap="5px" wrap="no-wrap">
                    {canUpdate && (
                      <div
                        className="icon"
                        style={{ background: '#65beee' }}
                        onClick={() => handleAction(el)}>
                        <EditIcon />
                      </div>
                    )}
                    {canDelete && (
                      <div
                        className="icon"
                        style={{ background: '#fb4a49' }}
                        onClick={() => handleAction(el, 'delete')}>
                        <DeleteIcon />
                      </div>
                    )}
                  </FlexWrapper>
                </FlexWrapper>
                <Description>{description(el?.note)}</Description>
                <SeeMore onClick={() => handleAction(el, 'view')}>See Full Details</SeeMore>
              </NotesCard>
            ))}
          </GridContainer>
          <PaginationBox>
            <Pagination pagination={pagination} hideOnSinglePage onChange={handleTableChange} />
          </PaginationBox>
        </>
      )}
    </>
  );
};

export default Feedback;
